import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { MenuItem, Select, SelectChangeEvent, Switch } from '@mui/material';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import LinearProgressWithLabel from '@/components/UI/ProgressBar/ProgressBar';
import { getAttachmentTag, getFileIcon, imgExtensionsFile, LOADING, LONG_FULL_DATE_FORMAT, ATTACHMENT_TAGS, statusCampaign, JV_VISIBLE } from '@/helpers/constants';
import { getFullFormattedDate } from '@/helpers/utils';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { Attachment } from '@/types';
import styles from './AttachmentFile.module.scss';

interface AttachmentFileProps {
  attachment: Attachment;
  checkDisable: boolean;
  canDelete: boolean;
  deleteBlocked: boolean;
  onFavorite?: (id: string) => void;
  onDownload?: (id: string) => void;
  onDelete?: (id: string) => void;
  isAffectedVehicles?: boolean;
  timeZone?: string;
  campaignInfo: { status: string };
  uploadProgress?: number;
  onChangeFileTags?: (id: string, field: 'jvVisible' | 'tag', value: string | boolean) => void;
  readOnlyTags?: boolean;
}

const AttachmentFile: React.FC<AttachmentFileProps> = ({
  attachment, checkDisable, canDelete, deleteBlocked,
  onFavorite = () => null, onDownload = () => null, onDelete = () => null,
  isAffectedVehicles, timeZone, campaignInfo, uploadProgress,
  onChangeFileTags, readOnlyTags,
}) => {
  const { t } = useTranslation();
  const isLoading = attachment.status === LOADING;
  const jvVisible = attachment.jointVenture ?? false;

  const content = (file: Attachment) => {
    if (imgExtensionsFile.includes(file.type)) {
      return file.thumbnail
        ? (
          <img
            className={styles.ImgAttachment}
            id="attachment-image"
            src={`data:image/${file.type};base64, ${file.thumbnail}`}
            alt={String(t('attachments.buttonUpload'))}
          />
        )
        : (
          <div className={styles.OtherFormat}>
            {t('attachments.thumbnailProcessed')}
          </div>
        );
    }

    return (
      <div className={styles.OtherFormat}>
        {file.type && (<i className={`di ${getFileIcon(file.type)} ${styles.IcoTypeFile}`} />)}
      </div>
    );
  };

  const showFullFormattedDate = (date: string) => {
    const myDateTime = new Date(date);

    return getFullFormattedDate(myDateTime, timeZone, LONG_FULL_DATE_FORMAT);
  };

  return (
    <div className={styles.Card}>
      <div className={styles.CardTitle}>
        {uploadProgress !== undefined
          ? <LinearProgressWithLabel value={uploadProgress} />
          : `${attachment.name}.${attachment.type}`}
        {onChangeFileTags && !isAffectedVehicles && jvVisible && (
          <div
            className={`${styles.JvBadge} ${styles.JvBadgeActive}`}
            title={String(t('attachments.jvVisibleYes'))}
          >
            JV
          </div>
        )}
      </div>
      <div className={styles.Container}>
        {content(attachment)}
        {attachment.favorite && !isAffectedVehicles && (
          <div className={styles.PositionButton}>
            <IcoButtonSimple
              disabled={checkDisable}
              id="btn-fav-attachment-added"
              icon="icon-stern-favorit"
            />
          </div>
        )}
        {!isLoading && (
          <div className={styles.ContainerInfo}>
            {showFullFormattedDate(attachment.date)}
            <div className={styles.ButtonsArea}>
              {['jpg', 'jpeg', 'png'].includes(attachment.type) && !isAffectedVehicles && (
                <IcoButtonSimple
                  disabled={checkDisable}
                  selected={attachment.favorite}
                  id="btn-fav-attachment"
                  icon="icon-stern-favorit"
                  handleClick={() => onFavorite(attachment.id)}
                />
              )}
              <IcoButtonSimple
                id="btn-download-attachment"
                icon="icon-auge-sichtbarkeit-an"
                handleClick={() => onDownload(attachment.id)}
              />
              {onChangeFileTags && !isAffectedVehicles && (
                <div className={styles.JvToggleWrapper}>
                  <span className={`${styles.JvToggleLabel} ${jvVisible ? styles.JvToggleLabelActive : ''}`}>
                    {t('attachments.jvVisibleYes')}
                  </span>
                  <Switch
                    id={`switch-jv-attachment-${attachment.id}`}
                    className={`${styles.JvSwitch} ${jvVisible ? styles.JvSwitchActive : ''}`}
                    checked={jvVisible}
                    onChange={(e) => onChangeFileTags?.(attachment.id, JV_VISIBLE, e.target.checked)}
                    disabled={readOnlyTags}
                    value={jvVisible}
                    inputProps={{
                      'aria-label': String(jvVisible ? t('attachments.jvVisibleYes') : t('attachments.jvVisibleNo')),
                    }}
                  />
                </div>
              )}
              {!deleteBlocked && (campaignInfo.status === statusCampaign.IN_CREATION || canDelete) && (
                <IcoButtonSimple
                  disabled={checkDisable}
                  id="btn-delete-attachment"
                  icon="icon-muelleimer-loeschen"
                  handleClick={() => onDelete(attachment.id)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {onChangeFileTags && !isAffectedVehicles && (
        <div className={styles.TagBadge}>
          <Select
            id={`select-tag-attachment-${attachment.id}`}
            value={getAttachmentTag(attachment as unknown as Record<string, unknown>)}
            onChange={(e: SelectChangeEvent) => onChangeFileTags(attachment.id, 'tag', e.target.value)}
            variant="standard"
            disableUnderline
            className={styles.TagSelect}
            size="small"
            disabled={readOnlyTags}
            renderValue={(selected) => (selected ? t(`attachments.tags.${(selected as string).toUpperCase()}`) : '')}
          >
            {Object.values(ATTACHMENT_TAGS).map((tagKey) => (
              <MenuItem key={tagKey} value={tagKey}>
                  {t(`attachments.tags.${tagKey.toUpperCase()}`, tagKey)}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  timeZone: getSettingsInfo(state)?.timezone,
  campaignInfo: getCampaignInfo(state),
});

export default connect(mapStateToProps)(AttachmentFile);
